import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Box, Button } from '@mui/material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'src/hooks/useDebounce';
import {
  downloadCFDIList,
  loadCFDIS,
  setCFDIsOptions,
  syncInvoicesAction
} from 'src/store/actions/cfdiActions';
import {
  FilterList,
  GetApp as Download,
  Loop as LoopIcon
} from '@mui/icons-material';
import Filter from 'src/components/Filter/Filter';
import { selectCFDISQueryOptions } from 'src/store/selectors/cfdiSelectors';
import { InvoiceTypes, IssuerType } from 'src/utils/constants/cfdi';
import { loadParameters } from 'src/store/actions/parameterActions';
import { ParameterSection } from 'src/utils/constants/parameters';
import { selectParameterList } from 'src/store/selectors/parameterSelector';
import {
  BillingSubSectionList,
  filterParameterByBillingSubSection
} from 'src/views/parameter/ParameterListView/BillingParameter/BillingParameterList';
import SearchBarByType from 'src/components/SearchBarByType';
import { CLEAR_CFDIS_OPTIONS, SYNC_INVOICES } from 'src/store/constants/CFDI';
import FILTER_TYPES from 'src/utils/constants/filter';
import selectFormatDate from 'src/utils/selectDateFormat';
import dayjs from 'src/utils/dayjs';
import dateFormatType from 'src/utils/dateFormatType';
import MenuButton from 'src/components/MenuButton/MenuButton';
import { CfdiActions } from 'src/utils/constants/clients';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import { selectAuthRole } from 'src/store/selectors/authSelectors';
import { UserRoles } from 'src/utils/constants/auth';
import useStyles from './styles';
import DownloadInvoicesListDialog from './DownloadInvoicesListDialog/DownloadInvoicesListDialog';

const searchTypes = [
  { value: 'client', label: 'NAME', type: 'text' },
  { value: 'folio', label: 'FOLIO', type: 'text' },
  { value: 'rfc', label: 'RFC', type: 'text' }
];
const CFDIMenu = [
  {
    label: 'COMMON.DOWNLOAD_INVOICES',
    key: CfdiActions.DOWNLOAD,
    icon: <Download />
  },
  {
    label: 'COMMON.DOWNLOAD_CFDIS',
    key: CfdiActions.DOWNLOAD_CFDI,
    icon: <Download />
  }
];
const selectCFDIActions = () => {
  const actions = CFDIMenu.filter(item => {
    return [CfdiActions.DOWNLOAD, CfdiActions.DOWNLOAD_CFDI].includes(item.key);
  });
  return actions;
};

const typeFilters = [
  {
    title: 'COMMON.DATE',
    controlGroup: FILTER_TYPES.DATE,
    items: [
      {
        label: 'COMMON.DATE',
        value: 'date',
        view: ['year', 'month']
      }
    ]
  },
  {
    title: 'COMMON.TYPE',
    controlGroup: FILTER_TYPES.CHECKBOX,
    items: [
      {
        label: 'ENUMS.INVOICE_TYPES.EXPENSE',
        value: InvoiceTypes.EXPENSE
      },
      {
        label: 'ENUMS.INVOICE_TYPES.INCOME',
        value: InvoiceTypes.INCOME
      },
      {
        label: 'ENUMS.INVOICE_TYPES.PAYMENT',
        value: InvoiceTypes.PAYMENT
      },
      {
        label: 'ENUMS.INVOICE_TYPES.PAYROLL',
        value: InvoiceTypes.PAYROLL
      },
      {
        label: 'ENUMS.INVOICE_TYPES.TRANSFER',
        value: InvoiceTypes.TRANSFER
      }
    ]
  }
];

function getFilterOptions(options, seriesOptions) {
  const type = [];
  const series = [];
  let month = null;
  let year = null;

  if (options[InvoiceTypes.EXPENSE]) {
    type.push(InvoiceTypes.EXPENSE);
  }
  if (options[InvoiceTypes.INCOME]) {
    type.push(InvoiceTypes.INCOME);
  }
  if (options[InvoiceTypes.PAYMENT]) {
    type.push(InvoiceTypes.PAYMENT);
  }
  if (options[InvoiceTypes.PAYROLL]) {
    type.push(InvoiceTypes.PAYROLL);
  }
  if (options[InvoiceTypes.TRANSFER]) {
    type.push(InvoiceTypes.TRANSFER);
  }

  seriesOptions.forEach(option => {
    if (options[option.value]) {
      series.push(option.value);
    }
  });

  if (options.date) {
    month = options.date.month() + 1;
    year = options.date.year();
  }

  return { type, series, month, year };
}

const Toolbar = ({ className, ...rest }) => {
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState('client');
  const [openDownloadInvoices, setOpenDownloadInvoices] = useState(false);
  const [openSyncDialog, setOpenSyncDialog] = useState(false);
  const userRole = useSelector(selectAuthRole);

  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectCFDISQueryOptions);
  const parameters = useSelector(selectParameterList);
  const isLoadingSync = useSelector(isLoadingSelector([SYNC_INVOICES.action]));
  const seriesOptions = filterParameterByBillingSubSection(
    parameters,
    BillingSubSectionList.INVOICE_TYPES
  ).map(parameter => {
    return {
      label: parameter.value,
      value: parameter.value
    };
  });

  const filters = [
    ...typeFilters,
    {
      title: 'COMMON.SERIE',
      controlGroup: 'checkbox',
      items: seriesOptions
    }
  ];

  const debounceSearch = useDebounce((search, queryOpts) => {
    if (search.rfc || search.folio || search.client) {
      dispatch(
        loadCFDIS({ ...queryOpts, ...search, page: 0, month: null, year: null })
      );
    } else {
      dispatch(
        loadCFDIS({
          ...queryOpts,
          client: '',
          rfc: '',
          folio: '',
          page: 0,
          month: queryOpts.month ?? new Date().getMonth() + 1,
          year: queryOpts.year ?? new Date().getFullYear()
        })
      );
    }
  }, 400);

  const handleCloseDownloadInvoices = () => {
    setOpenDownloadInvoices(false);
  };

  const handleOpenSyncDialog = () => {
    setOpenSyncDialog(true);
  };

  const handleCloseSyncDialog = () => {
    setOpenSyncDialog(false);
  };

  const handleSync = () => {
    const closeCallback = () => {
      handleCloseSyncDialog();
    };

    dispatch(
      syncInvoicesAction(
        {
          year: queryOptions.year,
          month: queryOptions.month
        },
        closeCallback
      )
    );
  };

  const handleSearch = useCallback(
    (e, criteria) => {
      const { value } = e.target;
      if (!value) {
        setSearchValue('');
        setFilterValues(prev => {
          const { date, ...otherFilters } = prev;
          return otherFilters;
        });
        debounceSearch({ [criteria]: '' }, queryOptions);
        return;
      }

      setSearchValue(value);
      setSearchCriteria(criteria);
      debounceSearch({ [criteria]: value }, queryOptions);
    },
    [setSearchValue, debounceSearch, searchCriteria]
  );

  const handleSearchCriteria = useCallback(() => {
    setSearchValue('');
    dispatch(
      setCFDIsOptions({
        ...queryOptions,
        [searchCriteria]: ''
      })
    );
  }, [dispatch, queryOptions]);

  const handleClickFilter = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleFilterChange = useCallback(
    event => {
      setFilterValues(event);
      debounceSearch(
        {},
        {
          ...queryOptions,
          page: 0,
          ...getFilterOptions(event, seriesOptions)
        }
      );
    },
    [
      setFilterValues,
      searchValue,
      debounceSearch,
      queryOptions,
      getFilterOptions,
      seriesOptions
    ]
  );

  useEffect(() => {
    dispatch(
      loadCFDIS({
        ...queryOptions,
        issuer: IssuerType.ISSUING,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      })
    );
    dispatch(
      loadParameters({
        section: ParameterSection.BILLING
      })
    );

    return () => {
      dispatch({ type: CLEAR_CFDIS_OPTIONS });
    };
  }, []);

  const handleClickMore = option => {
    if (option === CfdiActions.DOWNLOAD) {
      dispatch(
        downloadCFDIList({
          month: queryOptions.month ?? new Date().getMonth() + 1,
          year: queryOptions.year ?? new Date().getFullYear()
        })
      );
    }

    if (option === CfdiActions.DOWNLOAD_CFDI) {
      setOpenDownloadInvoices(true);
    }
  };

  return (
    <>
      <Box className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb doNotRedirectIndexList={[0]} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              style={{ fontWeight: '400', fontSize: '1.5rem' }}
              color="primary"
              variant="h1"
            >
              {t('NAVIGATION.CFDI_ISSUED')}
            </Typography>
            {queryOptions.month && queryOptions.year && (
              <Typography
                color="primary"
                variant="h3"
                style={{
                  fontWeight: '350',
                  marginLeft: '10px',
                  textAlign: 'end',
                  fontSize: '1.3rem'
                }}
              >
                {selectFormatDate(
                  dayjs(new Date(queryOptions.year, queryOptions.month - 1, 1)),
                  dateFormatType.MONTH_AND_YEAR
                )}
              </Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <SearchBarByType
              types={searchTypes}
              handleSearch={handleSearch}
              searchValue={searchValue}
              handleSearchCriteria={handleSearchCriteria}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Filter
                anchorEl={anchorEl}
                filterValues={filterValues}
                handleClose={handleCloseFilter}
                handleChange={handleFilterChange}
                filters={filters}
              />
              <Button
                className={clsx(
                  classes.buttons,
                  classes.outlined,
                  classes.horizontalPadding
                )}
                sx={{ marginRight: 2, marginLeft: 2 }}
                color="primary"
                onClick={handleClickFilter}
              >
                <FilterList className={classes.icon} />
                {t('COMMON.FILTER')}
              </Button>
              {userRole === UserRoles.SUPER_ADMIN && (
                <Button
                  className={clsx(
                    classes.buttons,
                    classes.outlined,
                    classes.horizontalPadding
                  )}
                  sx={{ marginRight: 2 }}
                  color="primary"
                  onClick={handleOpenSyncDialog}
                >
                  <LoopIcon className={classes.icon} />
                  {t('COMMON.SYNC')}
                </Button>
              )}
            </Box>
            <DownloadInvoicesListDialog
              open={openDownloadInvoices}
              onClose={handleCloseDownloadInvoices}
            />
            <MenuButton
              icon={<Download />}
              options={selectCFDIActions()}
              handleClickOption={handleClickMore}
              deleteDisabled
            />
          </Box>
        </Box>
      </Box>

      <ConfirmationDialog
        open={openSyncDialog}
        handleClose={handleCloseSyncDialog}
        onSubmit={handleSync}
        isLoading={isLoadingSync}
        title={t('CFDI_MANUAL_SYNC.TITLE')}
        description={t('CFDI_MANUAL_SYNC.DESCRIPTION', {
          month: selectFormatDate(
            momentLocalTimezone(
              new Date(queryOptions.year, queryOptions.month - 1, 1)
            ),
            dateFormatType.MONTH_AND_YEAR
          )
        })}
      />
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
